import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useAuth, getToken } from '../context/AuthContext';
import config from '../config';

// Brand purple/orange, lightened/darkened to pass colorblind + contrast checks.
const SERIES_A = '#5B4BA8';
const SERIES_B = '#E0601A';

const SEGMENTS = [
  { key: 'registered', label: 'Registered', filter: () => true },
  { key: 'paid', label: 'Paid', filter: (u) => u.subscription_status === 'active' },
  { key: 'legacy', label: 'Legacy', filter: (u) => u.is_legacy },
];

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : 'Never');
const shortDay = (day) => {
  const [, m, d] = day.split('-');
  return `${Number(m)}/${Number(d)}`;
};

function StatTile({ label, value, sub }) {
  return (
    <div className="admin-tile">
      <div className="admin-tile-label">{label}</div>
      <div className="admin-tile-value">{fmt(value)}</div>
      {sub && <div className="admin-tile-sub">{sub}</div>}
    </div>
  );
}

function signInMethod(u) {
  const methods = [];
  if (u.has_password) methods.push('Password');
  if (u.has_google) methods.push('Google');
  return methods.length ? methods.join(' + ') : 'Not set up';
}

export default function AdminPage() {
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [segment, setSegment] = useState('registered');

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch(`${config.apiBaseURL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.status === 404 || res.status === 401) { setStatus('notfound'); return; }
      if (!res.ok) throw new Error();
      setStats(await res.json());
      setStatus('ok');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    load();
  }, [user, authLoading, navigate, load]);

  if (status === 'notfound') {
    return <div className="admin-page"><p>Page not found.</p></div>;
  }
  if (status === 'error') {
    return (
      <div className="admin-page">
        <p className="error-message">Failed to load usage stats.</p>
        <button className="admin-refresh" onClick={load}>Retry</button>
      </div>
    );
  }
  if (!stats) {
    return <div className="admin-page"><p>Loading…</p></div>;
  }

  const { users: u, activity: a, daily, topPages, topSymbols, userList } = stats;
  const seg = SEGMENTS.find((s) => s.key === segment);
  const segmentUsers = userList.filter(seg.filter);
  const counts = { registered: u.registered, paid: u.paid, legacy: u.legacy };
  const chartData = daily.map((d) => ({ ...d, label: shortDay(d.day) }));

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>Usage</h2>
        <div className="admin-header-meta">
          Updated {new Date(stats.generatedAt).toLocaleTimeString()}
          <button className="admin-refresh" onClick={load} disabled={status === 'loading'}>
            {status === 'loading' ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <h3 className="admin-section-title">Users</h3>
      <div className="admin-tiles">
        <StatTile label="Registered" value={u.registered} sub={`+${fmt(u.signups_7d)} this week · +${fmt(u.signups_30d)} in 30d`} />
        <StatTile label="Paid" value={u.paid} sub={`${fmt(u.past_due)} past due · ${fmt(u.canceled)} canceled`} />
        <StatTile label="Legacy" value={u.legacy} sub={`${fmt(u.legacy_activated)} logged in · ${fmt(u.legacy_paid)} paid`} />
      </div>

      <h3 className="admin-section-title">Activity</h3>
      <div className="admin-tiles">
        <StatTile label="Page views (24h)" value={a.views_1d} sub={`${fmt(a.views_7d)} 7d · ${fmt(a.views_30d)} 30d`} />
        <StatTile label="Unique visitors (24h)" value={a.visitors_1d} sub={`${fmt(a.visitors_7d)} 7d · ${fmt(a.visitors_30d)} 30d`} />
        <StatTile label="Logins (24h)" value={a.logins_1d} sub={`${fmt(a.logins_7d)} 7d · ${fmt(a.logins_30d)} 30d`} />
        <StatTile label="Users logged in (24h)" value={a.dau} sub={`${fmt(a.wau)} 7d · ${fmt(a.mau)} 30d`} />
      </div>

      <div className="admin-charts">
        <div className="admin-card">
          <h4>Page views &amp; unique visitors, last 30 days</h4>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#eee" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7f8c8d' }} interval="preserveStartEnd" minTickGap={16} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#7f8c8d' }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="views" name="Page views" stroke={SERIES_A} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="visitors" name="Unique visitors" stroke={SERIES_B} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="admin-card">
          <h4>Logins &amp; signups, last 30 days</h4>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: -16, bottom: 0 }} barGap={2}>
              <CartesianGrid stroke="#eee" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7f8c8d' }} interval="preserveStartEnd" minTickGap={16} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#7f8c8d' }} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="logins" name="Logins" fill={SERIES_A} radius={[4, 4, 0, 0]} />
              <Bar dataKey="signups" name="Signups" fill={SERIES_B} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="admin-charts">
        <div className="admin-card">
          <h4>Top pages, 30 days</h4>
          <table className="watchlist-table admin-table">
            <thead><tr><th>Path</th><th className="num">Views</th><th className="num">Visitors</th></tr></thead>
            <tbody>
              {topPages.length === 0 && <tr><td colSpan={3}>No page views yet.</td></tr>}
              {topPages.map((p) => (
                <tr key={p.path}><td>{p.path}</td><td className="num">{fmt(p.views)}</td><td className="num">{fmt(p.visitors)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-card">
          <h4>Most viewed stocks, 30 days</h4>
          <table className="watchlist-table admin-table">
            <thead><tr><th>Symbol</th><th className="num">Views</th><th className="num">Visitors</th></tr></thead>
            <tbody>
              {topSymbols.length === 0 && <tr><td colSpan={3}>No stock views yet.</td></tr>}
              {topSymbols.map((s) => (
                <tr key={s.symbol}><td>{s.symbol}</td><td className="num">{fmt(s.views)}</td><td className="num">{fmt(s.visitors)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-segment-tabs" role="tablist">
          {SEGMENTS.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={segment === s.key}
              className={`admin-segment-tab${segment === s.key ? ' active' : ''}`}
              onClick={() => setSegment(s.key)}
            >
              {s.label} ({fmt(counts[s.key])})
            </button>
          ))}
        </div>
        <div className="admin-table-scroll">
          <table className="watchlist-table admin-table">
            <thead>
              <tr>
                <th>Email</th><th>Status</th><th>Sign-in</th><th>Joined</th><th>Last login</th><th className="num">Logins</th>
              </tr>
            </thead>
            <tbody>
              {segmentUsers.length === 0 && <tr><td colSpan={6}>No {seg.label.toLowerCase()} users.</td></tr>}
              {segmentUsers.map((x) => (
                <tr key={x.id}>
                  <td>
                    {x.email}
                    {x.is_legacy && <span className="admin-badge">Legacy</span>}
                  </td>
                  <td>{x.subscription_status === 'none' ? 'Free' : x.subscription_status.replace('_', ' ')}</td>
                  <td>{signInMethod(x)}</td>
                  <td>{fmtDate(x.created_at)}</td>
                  <td>{fmtDateTime(x.last_login_at)}</td>
                  <td className="num">{fmt(x.login_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="admin-footnote">
        Page views and logins are counted from when tracking was deployed. Legacy users are Proceemo
        subscribers imported by <code>migrateStripeSubscribers.js --apply</code>.
      </p>
    </div>
  );
}
